import React, { useEffect, useMemo, useState } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLifeRing, faSearch, faFolderOpen, faGears, faClock,
  faCheck, faBan, faCircleExclamation, faEye, faPaperPlane,
  faLock, faScrewdriverWrench,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import DOMPurify from 'dompurify';
import DataTable, { Column } from '../../components/shared/DataTable';
import RichTextEditor from '../../components/shared/RichTextEditor';
import TicketAttachmentUploader, { AttachmentItem } from '../../components/shared/TicketAttachmentUploader';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import { useServerPagination } from '../../hooks/useServerPagination';

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = 'Asia/Bangkok';
const today = () => dayjs().tz(TZ).format('YYYY-MM-DD');

// ─── Types ────────────────────────────────────────────────────────────────────

type TicketStatus   = 'open' | 'in_progress' | 'waiting_merchant' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
type TicketCategory = 'payin' | 'payout' | 'settlement' | 'other';

interface MerchantInfo { id?: string; name?: string; company_name?: string; email?: string }

interface TicketRow {
  id: string;
  ticket_number: string;
  merchant_id: string;
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  status: TicketStatus;
  assigned_admin_id: string | null;
  last_reply_at: string | null;
  last_reply_by_type: string | null;
  created_at: string;
  merchant: MerchantInfo | null;
}

interface TicketDetail {
  id: string;
  ticket_number: string;
  merchant_id: string;
  subject: string;
  description_html: string;
  category: string;
  priority: TicketPriority;
  status: TicketStatus;
  assigned_admin_id: string | null;
  created_at: string;
}
interface TicketMessage {
  id: string;
  sender_type: 'merchant' | 'employee' | 'admin' | 'admin_employee' | 'system';
  sender_name: string | null;
  body_html: string;
  is_internal_note: boolean;
  created_at: string;
}
interface RelatedOrder {
  id: number;
  ref_type: 'order' | 'payout' | 'settlement';
  ref_id: string;
  ref_number: string;
  ref_amount: number | null;
  ref_status: string | null;
}
interface TicketAttachment {
  id: string;
  ticket_id: string;
  message_id: string | null;
  file_url: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
}

// ─── Lookup maps ──────────────────────────────────────────────────────────────

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'เปิด', in_progress: 'กำลังดำเนินการ', waiting_merchant: 'รอ Merchant ตอบ',
  resolved: 'แก้ไขแล้ว', closed: 'ปิด',
};
const PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: 'ต่ำ', normal: 'ปกติ', high: 'สูง', urgent: 'ด่วน',
};
const PRIORITY_CLASS: Record<TicketPriority, string> = {
  low:    'text-slate-500 dark:text-slate-400',
  normal: 'text-slate-700 dark:text-slate-200',
  high:   'text-orange-600 dark:text-orange-400 font-semibold',
  urgent: 'text-rose-600 dark:text-rose-400 font-bold',
};
const CATEGORY_LABEL: Record<TicketCategory, string> = {
  payin: 'Payin', payout: 'Payout', settlement: 'Settlement', other: 'อื่นๆ',
};

const TICKET_STATUS_MAP: Record<string, StatusDef> = {
  open:             { bg: 'bg-blue-100',    text: 'text-blue-700',    icon: faFolderOpen, label: 'เปิด' },
  in_progress:      { bg: 'bg-amber-100',   text: 'text-amber-700',   icon: faGears,      label: 'กำลังดำเนินการ' },
  waiting_merchant: { bg: 'bg-purple-100',  text: 'text-purple-700',  icon: faClock,      label: 'รอ Merchant ตอบ' },
  resolved:         { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: faCheck,      label: 'แก้ไขแล้ว' },
  closed:           { bg: 'bg-slate-100',   text: 'text-slate-600',   icon: faBan,        label: 'ปิด' },
};

const TYPE_COLOR: Record<string, string> = {
  order:      'bg-blue-50 text-blue-700 border-blue-200',
  payout:     'bg-purple-50 text-purple-700 border-purple-200',
  settlement: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const SELECT_CLASS =
  'w-full text-sm px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(s?: string | null) {
  if (!s) return '-';
  return new Date(s).toLocaleString('th-TH', { dateStyle: 'short', timeStyle: 'short' });
}

function fmtMedium(s?: string | null) {
  if (!s) return '-';
  return new Date(s).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
}

const INPUT_CLASS =
  'px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 w-full';

const SafeHtml: React.FC<{ html: string }> = ({ html }) => {
  const clean = useMemo(() => DOMPurify.sanitize(html || '', { USE_PROFILES: { html: true } }), [html]);
  return <div className="prose prose-sm max-w-none break-words dark:prose-invert" dangerouslySetInnerHTML={{ __html: clean }} />;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const AdminTicketsPage: React.FC = () => {
  const {
    currentPage, pageSize,
    totalItems, setTotalItems,
    resetPage, serverPaginationProps,
  } = useServerPagination(10);

  const [tickets, setTickets]           = useState<TicketRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [stats, setStats]               = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Filters
  const [status,   setStatus]   = useState<TicketStatus | ''>('');
  const [priority, setPriority] = useState<TicketPriority | ''>('');
  const [category, setCategory] = useState<TicketCategory | ''>('');
  const [search,   setSearch]   = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Date draft / applied
  const [startDate,        setStartDate]        = useState(today);
  const [endDate,          setEndDate]          = useState(today);
  const [appliedStartDate, setAppliedStartDate] = useState(today);
  const [appliedEndDate,   setAppliedEndDate]   = useState(today);

  // Modal state
  type ModalMode = 'view' | 'manage';
  const [modalMode,        setModalMode]        = useState<ModalMode>('view');
  const [modalOpen,        setModalOpen]        = useState(false);
  const [modalLoading,     setModalLoading]     = useState(false);
  const [selectedId,       setSelectedId]       = useState<string | null>(null);
  const [modalTicket,      setModalTicket]      = useState<TicketDetail | null>(null);
  const [modalMerchant,    setModalMerchant]    = useState<MerchantInfo | null>(null);
  const [modalMessages,    setModalMessages]    = useState<TicketMessage[]>([]);
  const [modalRelated,     setModalRelated]     = useState<RelatedOrder[]>([]);
  const [modalAttachments, setModalAttachments] = useState<TicketAttachment[]>([]);

  // Reply state (manage mode)
  const [replyHtml,        setReplyHtml]        = useState('');
  const [replyAttachments, setReplyAttachments] = useState<AttachmentItem[]>([]);
  const [isInternal,       setIsInternal]       = useState(false);
  const [submitting,       setSubmitting]       = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); resetPage(); }, 300);
    return () => clearTimeout(t);
  }, [search, resetPage]);

  // Stats
  useEffect(() => {
    merchantApi.get('/admin/tickets/stats')
      .then(r => setStats(r.data?.data))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  // Tickets list
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    merchantApi.get('/admin/tickets', {
      params: {
        status:     status   || undefined,
        priority:   priority || undefined,
        category:   category || undefined,
        search:     debouncedSearch || undefined,
        start_date: debouncedSearch ? undefined : (appliedStartDate || undefined),
        end_date:   debouncedSearch ? undefined : (appliedEndDate   || undefined),
        limit:  pageSize,
        offset: (currentPage - 1) * pageSize,
      },
    })
      .then(res => {
        if (cancelled) return;
        setTickets(res.data?.data || []);
        setTotalItems(res.data?.total || 0);
      })
      .catch(() => { if (!cancelled) toast.error('โหลดล้มเหลว'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [status, priority, category, debouncedSearch, appliedStartDate, appliedEndDate, currentPage, pageSize, setTotalItems]);

  const handleSearch = () => {
    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    resetPage();
  };

  const handleReset = () => {
    const t = today();
    setSearch(''); setStatus(''); setPriority(''); setCategory('');
    setStartDate(t); setEndDate(t);
    setAppliedStartDate(t); setAppliedEndDate(t);
    resetPage();
  };

  // ─── Modal helpers ──────────────────────────────────────────────────────────

  const loadModalData = (id: string) => {
    setModalLoading(true);
    merchantApi.get(`/admin/tickets/${id}`)
      .then(res => {
        const d = res.data?.data;
        setModalTicket(d?.ticket || null);
        setModalMerchant(d?.merchant || null);
        setModalMessages(d?.messages || []);
        setModalRelated(d?.related_orders || []);
        setModalAttachments(d?.attachments || []);
      })
      .catch(() => toast.error('โหลดรายละเอียดไม่สำเร็จ'))
      .finally(() => setModalLoading(false));
  };

  const openModal = (id: string, mode: ModalMode) => {
    setSelectedId(id);
    setModalMode(mode);
    setModalOpen(true);
    setModalTicket(null);
    setModalMerchant(null);
    setModalMessages([]);
    setModalRelated([]);
    setModalAttachments([]);
    setReplyHtml('');
    setReplyAttachments([]);
    setIsInternal(false);
    loadModalData(id);
  };

  const updateTicketField = async (payload: Record<string, string>) => {
    if (!selectedId) return;
    try {
      const res = await merchantApi.patch(`/admin/tickets/${selectedId}`, payload);
      if (res.data?.success) {
        toast.success('อัปเดตแล้ว');
        loadModalData(selectedId);
      } else {
        toast.error(res.data?.message || 'เกิดข้อผิดพลาด');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'อัปเดตไม่สำเร็จ');
    }
  };

  const sendReply = async () => {
    const text = replyHtml.replace(/<[^>]+>/g, '').trim();
    if (!text) { toast.warn('กรุณากรอกข้อความ'); return; }
    if (!selectedId) return;
    setSubmitting(true);
    try {
      const res = await merchantApi.post(`/admin/tickets/${selectedId}/reply`, {
        body_html: replyHtml,
        is_internal_note: isInternal,
        attachments: replyAttachments.map(a => ({
          file_url: a.file_url,
          file_name: a.file_name,
          mime_type: a.mime_type,
          size_bytes: a.size_bytes,
        })),
      });
      if (res.data?.success) {
        toast.success(isInternal ? 'บันทึก internal note แล้ว' : 'ส่งตอบกลับเรียบร้อย');
        setReplyHtml('');
        setReplyAttachments([]);
        loadModalData(selectedId);
      } else {
        toast.error(res.data?.message || 'เกิดข้อผิดพลาด');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'ส่งไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  const ticketAttachments = useMemo(
    () => modalAttachments.filter(a => !a.message_id),
    [modalAttachments],
  );
  const messageAttachments = (mid: string) => modalAttachments.filter(a => a.message_id === mid);

  // ─── Stats cards ────────────────────────────────────────────────────────────

  const statsCards: StatCardDef[] = [
    {
      icon: faFolderOpen,
      iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-500 dark:text-blue-400',
      label: 'เปิด',
      value: <span className="text-blue-600 dark:text-blue-400">{stats?.open ?? 0}</span>,
    },
    {
      icon: faGears,
      iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400',
      label: 'ดำเนินการ',
      value: <span className="text-amber-600 dark:text-amber-400">{stats?.in_progress ?? 0}</span>,
    },
    {
      icon: faClock,
      iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400',
      label: 'รอ Merchant',
      value: <span className="text-purple-600 dark:text-purple-400">{stats?.waiting_merchant ?? 0}</span>,
    },
    {
      icon: faCheck,
      iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400',
      label: 'แก้ไขแล้ว',
      value: <span className="text-emerald-600 dark:text-emerald-400">{stats?.resolved ?? 0}</span>,
    },
    {
      icon: faBan,
      iconClassName: 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400',
      label: 'ปิด',
      value: <span className="text-gray-700 dark:text-slate-200">{stats?.closed ?? 0}</span>,
    },
    {
      icon: faCircleExclamation,
      iconClassName: 'bg-rose-50 dark:bg-rose-900/30 text-rose-500 dark:text-rose-400',
      label: 'ด่วน (ค้าง)',
      value: <span className="text-rose-600 dark:text-rose-400">{stats?.urgent_open ?? 0}</span>,
    },
  ];

  // ─── Table columns ───────────────────────────────────────────────────────────

  const columns: Column<TicketRow>[] = [
    {
      key: 'ticket_number',
      header: 'Ticket #',
      render: (t) => (
        <span className="font-mono text-xs text-gray-700 dark:text-slate-300">{t.ticket_number}</span>
      ),
    },
    {
      key: 'merchant',
      header: 'Merchant',
      render: (t) => (
        <div className="text-xs min-w-0">
          <div className="font-medium text-gray-800 dark:text-slate-100 truncate max-w-[180px]">
            {t.merchant?.company_name || t.merchant?.name || '-'}
          </div>
          {t.merchant?.email && (
            <div className="text-gray-400 dark:text-slate-500 truncate max-w-[180px]">{t.merchant.email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'หัวข้อ',
      render: (t) => (
        <span className="text-gray-800 dark:text-slate-100 font-medium text-sm truncate block max-w-[280px]">
          {t.subject}
        </span>
      ),
    },
    {
      key: 'category',
      header: 'หมวด',
      render: (t) => (
        <span className="text-xs text-gray-600 dark:text-slate-400">{CATEGORY_LABEL[t.category]}</span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (t) => (
        <span className={`text-xs ${PRIORITY_CLASS[t.priority]}`}>{PRIORITY_LABEL[t.priority]}</span>
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      render: (t) => <StatusBadge status={t.status} statusMap={TICKET_STATUS_MAP} />,
    },
    {
      key: 'last_reply_at',
      header: 'อัปเดต',
      render: (t) => (
        <span className="text-xs text-gray-500 dark:text-slate-400">{fmt(t.last_reply_at || t.created_at)}</span>
      ),
    },
    {
      key: 'actions' as any,
      header: 'Action',
      render: (t) => (
        <div className="flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); openModal(t.id, 'view'); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium
              bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400
              hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors
              inline-flex items-center gap-1 whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faEye} className="text-[10px]" />
            ดู
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); openModal(t.id, 'manage'); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium
              bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400
              hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors
              inline-flex items-center gap-1 whitespace-nowrap"
          >
            <FontAwesomeIcon icon={faScrewdriverWrench} className="text-[10px]" />
            จัดการ
          </button>
        </div>
      ),
    },
  ];

  // ─── Shared modal content ─────────────────────────────────────────────────────

  const renderModalContent = () => {
    if (modalLoading) {
      return <div className="py-12 text-center text-gray-400 dark:text-slate-500 text-sm">กำลังโหลด...</div>;
    }
    if (!modalTicket) {
      return <div className="py-12 text-center text-gray-400 dark:text-slate-500 text-sm">ไม่พบข้อมูล</div>;
    }

    return (
      <div className="space-y-4">

        {/* ── Subject + badges ── */}
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 leading-tight">
              {modalTicket.subject}
            </h2>
            <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">
              เปิดเมื่อ {fmtMedium(modalTicket.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5 shrink-0">
            <StatusBadge status={modalTicket.status} statusMap={TICKET_STATUS_MAP} />
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${PRIORITY_CLASS[modalTicket.priority]}`}>
              {PRIORITY_LABEL[modalTicket.priority]}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400">
              {CATEGORY_LABEL[modalTicket.category as TicketCategory] ?? modalTicket.category}
            </span>
          </div>
        </div>

        {/* ── Merchant + management controls (manage mode) ── */}
        <div className={`grid gap-3 ${modalMode === 'manage' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
          {modalMerchant && (
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-700">
              <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-1">Merchant</p>
              <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">
                {modalMerchant.company_name || modalMerchant.name || '-'}
              </p>
              {modalMerchant.email && (
                <p className="text-xs text-gray-500 dark:text-slate-400">{modalMerchant.email}</p>
              )}
            </div>
          )}
          {modalMode === 'manage' && (
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-700">
              <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-2">จัดการ</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
                  <select
                    value={modalTicket.status}
                    onChange={e => updateTicketField({ status: e.target.value })}
                    className={SELECT_CLASS}
                  >
                    {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 dark:text-slate-400 mb-1">Priority</label>
                  <select
                    value={modalTicket.priority}
                    onChange={e => updateTicketField({ priority: e.target.value })}
                    className={SELECT_CLASS}
                  >
                    {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Description ── */}
        <div className="p-4 rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800">
          <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-2">รายละเอียด</p>
          <SafeHtml html={modalTicket.description_html} />
          {ticketAttachments.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {ticketAttachments.map(a => (
                <a key={a.id} href={a.file_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600 rounded-lg text-xs text-gray-700 dark:text-slate-300 transition-colors">
                  📎 <span className="truncate max-w-[200px]">{a.file_name}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        {/* ── Related orders ── */}
        {modalRelated.length > 0 && (
          <div className="p-4 rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800">
            <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-2">Order ที่เกี่ยวข้อง</p>
            <div className="flex flex-wrap gap-1.5">
              {modalRelated.map(r => (
                <span key={r.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border ${TYPE_COLOR[r.ref_type] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                  <span className="font-semibold uppercase text-[9px]">{r.ref_type}</span>
                  <span className="font-mono">{r.ref_number}</span>
                  {typeof r.ref_amount === 'number' && (
                    <span>฿{Number(r.ref_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                  )}
                  {r.ref_status && <span className="opacity-60 text-[10px]">· {r.ref_status}</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ── Messages thread ── */}
        <div className="p-4 rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800">
          <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide mb-3">
            การสนทนา{modalMessages.length > 0 && ` (${modalMessages.length})`}
          </p>
          {modalMessages.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-4">ยังไม่มีการตอบกลับ</p>
          ) : (
            <div className="space-y-3">
              {modalMessages.map(m => {
                const isAdminSide = m.sender_type === 'admin' || m.sender_type === 'admin_employee';
                const isSystem    = m.sender_type === 'system';
                if (isSystem) {
                  return (
                    <div key={m.id} className="text-center text-xs text-gray-400 dark:text-slate-500">
                      <SafeHtml html={m.body_html} />
                      <span>· {fmt(m.created_at)}</span>
                    </div>
                  );
                }
                const msgAttachments = messageAttachments(m.id);
                return (
                  <div key={m.id} className={`flex ${isAdminSide ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      m.is_internal_note
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-900 dark:text-yellow-200'
                        : isAdminSide
                          ? 'bg-slate-100 dark:bg-slate-700 text-gray-800 dark:text-slate-100'
                          : 'bg-blue-600 text-white'
                    }`}>
                      <div className={`text-[11px] mb-1 ${
                        m.is_internal_note ? 'text-yellow-700 dark:text-yellow-400'
                          : isAdminSide ? 'text-gray-500 dark:text-slate-400'
                          : 'text-blue-100'
                      }`}>
                        {m.is_internal_note && <FontAwesomeIcon icon={faLock} className="mr-1" />}
                        {m.is_internal_note ? 'Internal Note · ' : ''}
                        {m.sender_name || m.sender_type} · {fmt(m.created_at)}
                      </div>
                      <SafeHtml html={m.body_html} />
                      {msgAttachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {msgAttachments.map(a => (
                            <a key={a.id} href={a.file_url} target="_blank" rel="noopener noreferrer"
                               className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] transition-colors ${
                                 isAdminSide
                                   ? 'bg-white dark:bg-slate-600 hover:bg-gray-50 dark:hover:bg-slate-500 border border-gray-200 dark:border-slate-500 text-gray-700 dark:text-slate-300'
                                   : 'bg-blue-700 hover:bg-blue-800 text-white'
                               }`}>
                              📎 <span className="truncate max-w-[160px]">{a.file_name}</span>
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Reply section (manage mode only) ── */}
        {modalMode === 'manage' && (
          <div className="p-4 rounded-lg border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wide">ตอบกลับ</p>
              <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={e => setIsInternal(e.target.checked)}
                  className="rounded"
                />
                <FontAwesomeIcon icon={faLock} className="text-amber-500 text-[10px]" />
                Internal note (Merchant มองไม่เห็น)
              </label>
            </div>
            <RichTextEditor
              value={replyHtml}
              onChange={setReplyHtml}
              placeholder="พิมพ์คำตอบ..."
              minHeight={120}
            />
            <TicketAttachmentUploader
              value={replyAttachments}
              onChange={setReplyAttachments}
              endpoint="/admin/tickets/upload"
            />
            <div className="flex justify-end">
              <UiButton icon={faPaperPlane} loading={submitting} onClick={sendReply}>
                {isInternal ? 'บันทึก Note' : 'ส่งตอบกลับ'}
              </UiButton>
            </div>
          </div>
        )}

      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-full overflow-x-hidden">

      <UiPageHeader
        icon={faLifeRing}
        title="Support Tickets"
        subtitle={`จัดการ ticket จาก merchants • ${totalItems.toLocaleString()} รายการ`}
      />

      <UiStatsGrid cards={statsCards} loading={statsLoading} cols={3} className="mb-6" />

      <UiFilterPanel
        dateFrom={startDate}
        dateTo={endDate}
        onDateFromChange={setStartDate}
        onDateToChange={setEndDate}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <div className="relative flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <FontAwesomeIcon icon={faSearch} className="mr-1" />
            ค้นหา
          </label>
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs pointer-events-none"
            />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="ค้นหา ticket / หัวข้อ..."
              className="pl-9 pr-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 w-full"
            />
          </div>
        </div>

        <div className="min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select value={status} onChange={e => { setStatus(e.target.value as any); resetPage(); }} className={INPUT_CLASS}>
            <option value="">ทุกสถานะ</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <div className="min-w-[120px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Priority</label>
          <select value={priority} onChange={e => { setPriority(e.target.value as any); resetPage(); }} className={INPUT_CLASS}>
            <option value="">ทุก priority</option>
            {Object.entries(PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>

        <div className="min-w-[120px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">หมวด</label>
          <select value={category} onChange={e => { setCategory(e.target.value as any); resetPage(); }} className={INPUT_CLASS}>
            <option value="">ทุกหมวด</option>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </UiFilterPanel>

      <DataTable
        data={tickets}
        columns={columns}
        loading={loading}
        keyExtractor={(t) => t.id}
        compact
        hoverable
        emptyMessage="ไม่พบ ticket"
        serverPagination={serverPaginationProps}
        headerContent={
          <h2 className="font-semibold text-gray-700 dark:text-slate-200 text-sm uppercase tracking-wide">
            รายการ Tickets
            {totalItems > 0 && (
              <span className="ml-2 text-gray-400 dark:text-slate-500 font-normal normal-case">({totalItems.toLocaleString()} รายการ)</span>
            )}
          </h2>
        }
      />

      {/* ─── Modal ─────────────────────────────────────────────────────────────── */}
      <UiModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={
          modalTicket
            ? `${modalMode === 'manage' ? 'จัดการ' : 'รายละเอียด'} Ticket #${modalTicket.ticket_number}`
            : modalMode === 'manage' ? 'จัดการ Ticket' : 'รายละเอียด Ticket'
        }
        size="xl"
        scrollable
      >
        {renderModalContent()}
      </UiModal>

    </div>
  );
};

export default AdminTicketsPage;
